import { CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import React from 'react';
import { useTableSelector } from 'hooks/useTableSelector';

/** Parse "HH:MM:SS" or "MM:SS" into total seconds. Returns 0 for missing/invalid. */
const parseCountdown = (val: any): number => {
  if (typeof val === 'number') return Number.isFinite(val) ? Math.max(0, val) : 0;
  if (typeof val !== 'string' || val === '-' || val === '') return 0;
  const parts = val.split(':').map(Number);
  if (parts.some((n) => Number.isNaN(n))) return 0;
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return 0;
};

/** Format seconds into the full "HH:MM:SS" level-clock format (always 3 fields). */
export const formatCountdown = (secs: number): string => {
  const total = Math.max(0, secs);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
};

// `toChips`, `compactChips`, and `formatBlinds` were lifted into
// `utils/functions.ts` so the same K / M truncation feeds both this
// tournament level readout AND the in-table stack / bet formatter
// (tableMoneyFormatterCompact). Re-exported here so existing consumers of
// this module (TableDetails.tsx, TournamentDetails.tsx, unit tests)
// continue to import from the same path. Local import + re-export shape
// keeps `formatBlinds` available inside this file too (used further down).
import { toChips, compactChips, formatBlinds } from 'utils/functions';
export { toChips, compactChips, formatBlinds };

const TournamentTableDetails = () => {
  const game = useTableSelector((table) => table.game);
  const tournamentStatus = useTableSelector((table) => table.tournamentStatus);

  // Client-side tick: sync from server every time tournamentStatus updates,
  // tick down locally every second so the countdown reads smoothly between pushes.
  const [localLevelCountdown, setLocalLevelCountdown] = useState(0);
  // Only the setter is needed — the elapsed value feeds the sync-drift decision
  // via the functional updater, it isn't displayed.
  const [, setLocalElapsedTime] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Read the latest server-side values for this tournament. We track elapsedTime
  // alongside levelCountDown — elapsedTime is monotonic during play and is the
  // canonical clock we use to make a single sync-vs-hold decision (so we don't
  // get the level countdown ticking out of step with the server's own clock).
  const { serverLevelCountdown, serverElapsedTime } = useMemo(() => {
    if (!tournamentStatus || tournamentStatus.length === 0) {
      return { serverLevelCountdown: 0, serverElapsedTime: 0 };
    }
    const match = tournamentStatus.find((d: any) => d.tournamentId === game.tournamentId) as any;
    return {
      serverLevelCountdown: parseCountdown(match?.levelCountDown),
      serverElapsedTime: Number(match?.elapsedTime) || 0
    };
  }, [tournamentStatus, game.tournamentId]);

  const isPlaying = game.status === 'P';
  const isPaused = !!game.isOnBreak || !!game.isMultiFlightBreak || !!game.isPostBalancing;

  // Atomic sync — decide ONCE based on elapsedTime drift, then apply to both
  // local timers together. Prevents the level countdown from drifting 1s out of
  // step with the server. (Same pattern as TournamentDetails.tsx.)
  useEffect(() => {
    if (!tournamentStatus || tournamentStatus.length === 0) return;
    const THRESHOLD = 2;

    setLocalElapsedTime((localElapsed) => {
      const isFirstSync = localElapsed === 0 && serverElapsedTime > 0;
      const elapsedDelta = serverElapsedTime - localElapsed; // +ve = server ahead
      const shouldSync = isFirstSync || elapsedDelta >= 1 || Math.abs(elapsedDelta) > THRESHOLD;

      if (shouldSync) {
        setLocalLevelCountdown(serverLevelCountdown);
        return serverElapsedTime;
      }
      return localElapsed;
    });
  }, [serverLevelCountdown, serverElapsedTime, tournamentStatus]);

  // Tick every second while the tournament is actively running. Stop ticking on break
  // / post-balancing / non-playing — both values freeze together until the next sync.
  useEffect(() => {
    if (!isPlaying || isPaused) return;
    timerRef.current = setInterval(() => {
      setLocalLevelCountdown((v) => Math.max(0, v - 1));
      setLocalElapsedTime((v) => v + 1);
    }, 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isPlaying, isPaused]);

  const displayCountdown = formatCountdown(localLevelCountdown);

  return game.tabletype !== 's' ? (
    <div style={TournamentTableDetailsContainerStyle}>
      {/* Tournament name (e.g. "SNG 3P FIFO", "Zero Degrees") — drives the
          at-a-glance "which tournament is this" the table was missing. */}
      {game.title ? <p style={tournamentNameStyle}>{game.title}</p> : null}
      <p>{game.lvlDisplay === 0 ? 'Break' : 'Level ' + game.lvlDisplay}</p>
      <p>{Number(game?.nextLevel) === -1 ? 'Max Level' : displayCountdown}</p>
      <p>
        {game.isMultiFlightBreak
          ? '-'
          : formatBlinds(game.currentSB, game.currentBB, game.currentAnte)}
      </p>
      <p>
        {' '}
        Next Level:{' '}
        {game.isMultiFlightBreak
          ? '-'
          : game.isOnBreak && game.isMultiFlight === 1 && game.isFinalMultiFlight === 1
          ? formatBlinds(game.sb, game.bb, game.ante)
          : game.nextLevelIsBreak === 1
          ? 'Break'
          : Number(game?.nextLevel) !== -1
          ? formatBlinds(game.nextSB, game.nextBB, game.nextAnte)
          : 'None'}
      </p>
    </div>
  ) : (
    <></>
  );
};

export default TournamentTableDetails;

const tournamentNameStyle: CSSProperties = {
  fontWeight: 700,
  color: 'rgba(255, 255, 255, 0.8)'
};

const TournamentTableDetailsContainerStyle: CSSProperties = {
  textAlign: 'center',
  fontFamily: 'Outfit',
  fontWeight: 500,
  fontStyle: 'normal',
  fontSize: '0.65rem',
  lineHeight: 1.4,
  color: 'rgba(255, 255, 255, 0.5)',
  whiteSpace: 'nowrap',
  zIndex: 2
};
